/**
 * The approvals queue (BIBLE §18, plan §2.3): what the agent has prepared, and the
 * operator's decision on it.
 *
 * Approving runs the very service the console's own button would, so what it touches is
 * decided by the proposal's kind — a campaign, a lead, a thread, a mailbox. The endpoint
 * does not know which without reading the proposal, and a missed refresh is worse than an
 * extra one, so a successful approval invalidates every tag any kind could move.
 */

import { api } from '../api'
import type { components } from '../schema'

type S = components['schemas']

export type Proposal = S['Proposal']
export type ProposalParams = S['ProposalParams']
export type ProposalKind = ProposalParams['kind']
export type ProposalStatus = S['ProposalStatus']
export type ListProposalsResult = S['ListProposalsResult']
export type ApproveProposalResult = S['ApproveProposalResult']
export type RejectProposalResult = S['RejectProposalResult']

/**
 * `status` for one status; `decided` for a side of the queue — `true` is every status a
 * proposal ends in, `false` those still awaiting a person. Not both: the backend refuses it.
 */
export type ListProposalsArgs = { limit?: number } & (
  | { status?: ProposalStatus; decided?: never }
  | { decided?: boolean; status?: never }
)

/** Everything an approved proposal of any kind can change. */
const ANYTHING_AN_ACTION_MOVES = [
  'Campaign',
  'CampaignStats',
  'Schedule',
  'Activity',
  'Lead',
  'Account',
  'Conversation',
  'Thread',
  'InboxFolder',
  'CampaignAnalytics',
  'EmailOverview',
  'Warmup',
] as const

export const proposalsApi = api.injectEndpoints({
  endpoints: (build) => ({
    listProposals: build.query<ListProposalsResult, ListProposalsArgs | void>({
      query: (args) => ({
        url: '/proposals',
        params: {
          ...(args?.status ? { status: args.status } : {}),
          ...(args?.decided !== undefined ? { decided: String(args.decided) } : {}),
          ...(args?.limit ? { limit: args.limit } : {}),
        },
      }),
      providesTags: (result) => [
        { type: 'Proposal', id: 'LIST' },
        ...(result?.items ?? []).map((proposal) => ({ type: 'Proposal' as const, id: proposal.id })),
      ],
    }),

    getProposal: build.query<{ proposal: Proposal }, string>({
      query: (id) => `/proposals/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'Proposal', id }],
    }),

    approveProposal: build.mutation<ApproveProposalResult, { id: string; body?: string }>({
      // `body` is send_reply's only: the operator's edit of the agent's draft.
      query: ({ id, body }) => ({
        url: `/proposals/${id}/approve`,
        method: 'POST',
        body: body !== undefined ? { body } : {},
      }),
      // A refusal (`failed`) is still a 200 and may have committed part of its work, so
      // any answer refreshes everything. A 503/500 left the proposal pending and undid the
      // action: only the queue needs a look.
      invalidatesTags: (result, _error, { id }) => [
        { type: 'Proposal', id: 'LIST' },
        { type: 'Proposal', id },
        ...(result ? ANYTHING_AN_ACTION_MOVES : []),
      ],
    }),

    rejectProposal: build.mutation<RejectProposalResult, { id: string; reason?: string }>({
      query: ({ id, reason }) => ({
        url: `/proposals/${id}/reject`,
        method: 'POST',
        body: reason ? { reason } : {},
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Proposal', id: 'LIST' },
        { type: 'Proposal', id },
      ],
    }),
  }),
})

export const {
  useListProposalsQuery,
  useGetProposalQuery,
  useApproveProposalMutation,
  useRejectProposalMutation,
} = proposalsApi
